'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { Button } from '@/src/shared/ui/button';
import { Input } from '@/src/shared/ui/input';
import { Label } from '@/src/shared/ui/label';

import { forgotPasswordSchema } from '../model/schemas';
import type { ForgotPasswordFormValues, ForgotPasswordState } from '../model/types';
import { forgotPasswordAction } from '../api/forgotPasswordAction';
import { ForgotPasswordSuccessPanel } from './ForgotPasswordSuccessPanel';

export function ForgotPasswordForm() {
  const t = useTranslations('auth.forgotPassword');
  const params = useParams();
  const locale = (params.locale as string) ?? 'es';

  const [state, setState] = useState<ForgotPasswordState>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({ resolver: yupResolver(forgotPasswordSchema) });

  const emailError = errors.email?.message;

  function getEmailErrorMessage(code: string | undefined): string | undefined {
    if (!code) return undefined;
    const map: Record<string, string> = {
      required: t('errors.email.required'),
      invalid: t('errors.email.invalid'),
    };
    return map[code] ?? code;
  }

  function apiErrorMessage(code: string): string {
    switch (code) {
      case 'rate_limited':
        return t('errors.api.rate_limited');
      default:
        return t('errors.api.server_error');
    }
  }

  async function onSubmit(data: ForgotPasswordFormValues) {
    setState('submitting');
    try {
      const result = await forgotPasswordAction(data);
      if (result && !result.ok) {
        toast.error(apiErrorMessage(result.code));
        setState('error');
        return;
      }
      setState('success');
    } catch {
      toast.error(apiErrorMessage('server_error'));
      setState('error');
    }
  }

  if (state === 'success') {
    return <ForgotPasswordSuccessPanel locale={locale} />;
  }

  const submitting = state === 'submitting';

  return (
    <form
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="forgot-email" className="text-xs font-medium text-muted-foreground">
          {t('fields.email.label')}
        </Label>
        <Input
          id="forgot-email"
          type="email"
          autoComplete="username"
          placeholder={t('fields.email.placeholder')}
          aria-invalid={!!emailError}
          aria-describedby={emailError ? 'forgot-email-error' : undefined}
          className="h-11 border-border/80 bg-background"
          {...register('email')}
        />
        {emailError && (
          <p id="forgot-email-error" role="alert" className="text-xs text-destructive">
            {getEmailErrorMessage(emailError)}
          </p>
        )}
      </div>

      <Button type="submit" disabled={submitting} className="h-11 w-full rounded-lg text-[14px]">
        {submitting ? (
          <>
            <Loader2 className="animate-spin" />
            {t('submitting')}
          </>
        ) : (
          t('submit')
        )}
      </Button>

      <div className="text-center text-xs text-muted-foreground">
        <Link
          href={`/${locale}/login`}
          className="hover:text-foreground underline-offset-4 hover:underline"
        >
          {t('links.login')}
        </Link>
      </div>
    </form>
  );
}
